import { useState } from 'react'
import TmdbFooter from './TmdbFooter'
import { findPartyByCode } from '../utils/partyGame'
import { ensureAnonymousAuth } from '../firebase'
import { IconSwords, IconHandshake, IconConfetti, IconScroll, IconTicket, IconChevronRight, IconX, IconPlay } from './icons'

export default function Multiplayer({ onStartH2H, onStartCoop, onStartParty, onJoinLobby, onBack, tickets = 0, onOpenStore }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const [joining, setJoining] = useState(false)
  const [creating, setCreating] = useState(false)
  const [creatingCoop, setCreatingCoop] = useState(false)
  const [creatingParty, setCreatingParty] = useState(false)

  // null | 'coop-create' | 'join' | 'party-create'
  const [confirmDialog, setConfirmDialog] = useState(null)

  // ── H2H create — show confirm first ─────────────────────────────────────
  const handleH2HCreateClick = () => {
    if (creating) return
    setConfirmDialog('h2h-create')
  }

  const handleH2HCreateConfirm = async () => {
    setConfirmDialog(null)
    setCreating(true)
    await onStartH2H()
    setCreating(false)
  }

  // ── Co-Op create — show confirm first ────────────────────────────────────
  const handleCoopCreateClick = () => {
    if (creatingCoop) return
    setConfirmDialog('coop-create')
  }

  const handleCoopCreateConfirm = async () => {
    setConfirmDialog(null)
    setCreatingCoop(true)
    await onStartCoop()
    setCreatingCoop(false)
  }

  // ── Party Mode ────────────────────────────────────────────────────────────
  // Host is never charged a ticket — players are charged when game starts.
  // The host experience opens in a separate TV-sized popup so the current window
  // stays free for the user to join the party as a player with their real profile.
  const handlePartyCreateClick = () => {
    if (creatingParty) return
    setErr('')
    const url = window.location.origin + window.location.pathname + '?host=1'
    const width = 1280
    const height = 900
    const left = Math.max(0, (window.screen.availWidth - width) / 2)
    const top = Math.max(0, (window.screen.availHeight - height) / 2)
    const popup = window.open(
      url,
      'action-party-host',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`,
    )
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      // Popup blocked — fall back to in-window host flow with a guidance message
      setErr('Please allow popups for the best party experience.')
      ;(async () => {
        setCreatingParty(true)
        await onStartParty?.()
        setCreatingParty(false)
      })()
    }
  }

  // ── Join — validate, then skip confirm for Party codes (ticket charged later)
  const handleJoinClick = async () => {
    setErr('')
    const trimmed = code.trim().toUpperCase()
    if (trimmed.length !== 6) {
      setErr('Game code must be 6 characters')
      return
    }
    // Auth FIRST — the /parties rules require request.auth != null even for the
    // findPartyByCode READ below, so a guest needs an (anonymous) identity before
    // we look the code up. Without it the read throws and a valid party code gets
    // misrouted into the game-code ticket dialog.
    await ensureAnonymousAuth()
    // Pre-check: if this is a Party code, bypass the ticket confirm dialog and
    // join immediately — Party tickets are charged at the pre-start "Let's Go!" step.
    try {
      const partyDoc = await findPartyByCode(trimmed)
      if (partyDoc) {
        setJoining(true)
        const error = await onJoinLobby(trimmed)
        setJoining(false)
        if (error) setErr(error)
        return
      }
    } catch {
      // Lookup failed — fall through to the normal confirm dialog
    }
    setConfirmDialog('join')
  }

  const handleJoinConfirm = async () => {
    setConfirmDialog(null)
    const trimmed = code.trim().toUpperCase()
    setJoining(true)
    const error = await onJoinLobby(trimmed)
    setJoining(false)
    if (error) setErr(error)
    // On success App.jsx navigated away
  }

  const closeDialog = () => setConfirmDialog(null)

  // Enter / Space activate a card just like a click (keyboard accessibility).
  const cardKey = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn() }
  }

  return (
    <div className="mp-screen">
      <div className="screen-content">
        <div className="page-title">
          <h1 className="brand-logo">Multiplayer</h1>
          <div className="page-title__sub">Head to Head · Co-op</div>
        </div>

        <div className="mp-modes">
          <div
            className="mp-card"
            role="button"
            tabIndex={0}
            onClick={handleH2HCreateClick}
            onKeyDown={cardKey(handleH2HCreateClick)}
            aria-disabled={creating || undefined}
            aria-label="Challenge a Friend — head-to-head"
          >
            <div className="mp-card__icon" aria-hidden="true"><IconSwords size={22} /></div>
            <div className="mp-card__title">Challenge a Friend</div>
            <div className="mp-card__subtitle">Head-to-Head</div>
            <div className="mp-card__cta-row">
              {creating ? <span>Creating…</span> : (
                <>
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket per player <span className="sep">·</span> Tap to begin</span>
                  <IconChevronRight size={13} aria-hidden="true" />
                </>
              )}
            </div>
          </div>

          <div
            className="mp-card"
            role="button"
            tabIndex={0}
            onClick={handleCoopCreateClick}
            onKeyDown={cardKey(handleCoopCreateClick)}
            aria-disabled={creatingCoop || undefined}
            aria-label="Co-op — team up to solve together"
          >
            <div className="mp-card__icon" aria-hidden="true"><IconHandshake size={22} /></div>
            <div className="mp-card__title">Co-op</div>
            <div className="mp-card__subtitle">Team up to solve together</div>
            <div className="mp-card__cta-row">
              {creatingCoop ? <span>Creating…</span> : (
                <>
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket per player <span className="sep">·</span> Tap to begin</span>
                  <IconChevronRight size={13} aria-hidden="true" />
                </>
              )}
            </div>
          </div>

          <div
            className="mp-card mp-card--party"
            role="button"
            tabIndex={0}
            onClick={handlePartyCreateClick}
            onKeyDown={cardKey(handlePartyCreateClick)}
            aria-disabled={creatingParty || undefined}
            aria-label="Party Mode — 3 to 12 players on the big screen"
          >
            <div className="mp-card__icon" aria-hidden="true"><IconConfetti size={22} /></div>
            <div className="mp-card__title">Party Mode</div>
            <div className="mp-card__subtitle">3–12 players · Big screen fun</div>
            <div className="mp-card__cta-row">
              {creatingParty ? <span>Starting party…</span> : (
                <>
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket per player <span className="sep">·</span> Tap to begin</span>
                  <IconChevronRight size={13} aria-hidden="true" />
                </>
              )}
            </div>
          </div>

          <div className="mp-card mp-card--join">
            <div className="mp-card__icon" aria-hidden="true"><IconScroll size={22} /></div>
            <div className="mp-card__title">Have a game code?</div>
            <div className="mp-card__subtitle">Join a friend's party or match</div>
            <div className="mp-card__code-row">
              <input
                className="mp-card__code-input"
                type="text"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))
                }
                onKeyDown={(e) => e.key === 'Enter' && !joining && handleJoinClick()}
                placeholder="FILM42"
                maxLength={6}
                autoCapitalize="characters"
                autoComplete="off"
                disabled={joining}
                aria-label="Game code"
              />
              <button
                type="button"
                className="mp-card__join-btn"
                onClick={handleJoinClick}
                disabled={code.length !== 6 || joining}
              >
                {joining ? 'Joining…' : 'Join'}
              </button>
            </div>
            {err && <p className="mp-join__err">{err}</p>}
          </div>
        </div>

        <TmdbFooter />
      </div>

      {/* ── Ticket confirmation dialogs ── */}
      {confirmDialog && (
        <div className="cue-card-confirm-overlay" onClick={closeDialog}>
          <div className="cue-card-confirm-dialog" onClick={(e) => e.stopPropagation()}>

            {confirmDialog === 'h2h-create' && (
              <>
                <p className="cue-card-confirm-dialog__title">Start H2H</p>
                <p className="cue-card-confirm-dialog__msg">
                  Challenge a friend to a head-to-head race.
                </p>
                <p className="cue-card-confirm-dialog__cost">
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket · You have {tickets.toLocaleString()}</span>
                </p>
                {tickets >= 1 ? (
                  <div className="confirm__actions">
                    <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                      <IconX size={20} />
                    </button>
                    <button className="confirm__confirm" aria-label="Confirm" onClick={handleH2HCreateConfirm}>
                      <IconPlay size={20} />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="cue-card-confirm-dialog__broke">Not enough tickets</p>
                    <div className="confirm__actions">
                      <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                        <IconX size={20} />
                      </button>
                      <button className="confirm__get-tickets" onClick={() => { closeDialog(); onOpenStore?.() }}>
                        Get Tickets
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {confirmDialog === 'coop-create' && (
              <>
                <p className="cue-card-confirm-dialog__title">Start Co-Op</p>
                <p className="cue-card-confirm-dialog__msg">
                  Create a lobby for your co-star to join. You'll work together to reach the goal actor!
                </p>
                <p className="cue-card-confirm-dialog__cost">
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket · You have {tickets.toLocaleString()}</span>
                </p>
                {tickets >= 1 ? (
                  <div className="confirm__actions">
                    <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                      <IconX size={20} />
                    </button>
                    <button className="confirm__confirm" aria-label="Confirm" onClick={handleCoopCreateConfirm}>
                      <IconPlay size={20} />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="cue-card-confirm-dialog__broke">Not enough tickets</p>
                    <div className="confirm__actions">
                      <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                        <IconX size={20} />
                      </button>
                      <button className="confirm__get-tickets" onClick={() => { closeDialog(); onOpenStore?.() }}>
                        Get Tickets
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {confirmDialog === 'join' && (
              <>
                <p className="cue-card-confirm-dialog__title">Join Game</p>
                <p className="cue-card-confirm-dialog__msg">
                  Joining will cost 1 ticket. Continue?
                </p>
                <p className="cue-card-confirm-dialog__cost">
                  <IconTicket size={13} aria-hidden="true" />
                  <span>1 Ticket · You have {tickets.toLocaleString()}</span>
                </p>
                {tickets >= 1 ? (
                  <div className="confirm__actions">
                    <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                      <IconX size={20} />
                    </button>
                    <button className="confirm__confirm" aria-label="Confirm" onClick={handleJoinConfirm}>
                      <IconPlay size={20} />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="cue-card-confirm-dialog__broke">Not enough tickets</p>
                    <div className="confirm__actions">
                      <button className="confirm__cancel" aria-label="Cancel" onClick={closeDialog}>
                        <IconX size={20} />
                      </button>
                      <button className="confirm__get-tickets" onClick={() => { closeDialog(); onOpenStore?.() }}>
                        Get Tickets
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

          </div>
        </div>
      )}
    </div>
  )
}
